/**
 * Granular Role-Based Access Control (RBAC) helpers for Ganvil Cloud.
 * Enforces role hierarchy: owner > admin > maintainer > member > viewer.
 */

import { currentUser } from "./auth";

export type Role = "owner" | "admin" | "maintainer" | "member" | "viewer" | "unknown";

export function normalizeRole(role?: string): Role {
  if (!role) return "viewer";
  const r = role.trim().toLowerCase();
  if (r === "owner") return "owner";
  if (r === "admin" || r === "administrator" || r === "dev") return "admin";
  if (r === "maintainer") return "maintainer";
  if (r === "member" || r === "write" || r === "writer" || r === "developer" || r === "contributor") return "member";
  if (r === "viewer" || r === "read" || r === "reader") return "viewer";
  return "viewer";
}

export function effectiveUserRole(explicitRoles?: string[]): Role {
  const roles = explicitRoles ?? currentUser()?.roles ?? [];
  if (roles.length === 0) return "viewer";

  const normalized = roles.map(normalizeRole);
  if (normalized.includes("owner")) return "owner";
  if (normalized.includes("admin")) return "admin";
  if (normalized.includes("maintainer")) return "maintainer";
  if (normalized.includes("member")) return "member";
  return "viewer";
}

export function canAdmin(role?: string): boolean {
  const r = normalizeRole(role ?? effectiveUserRole());
  return r === "owner" || r === "admin";
}

export function canPublishRelease(role?: string): boolean {
  const r = normalizeRole(role ?? effectiveUserRole());
  return r === "owner" || r === "admin" || r === "maintainer";
}

export function canMerge(role?: string): boolean {
  const r = normalizeRole(role ?? effectiveUserRole());
  return r === "owner" || r === "admin" || r === "maintainer";
}

export function canWrite(role?: string): boolean {
  const r = normalizeRole(role ?? effectiveUserRole());
  return r === "owner" || r === "admin" || r === "maintainer" || r === "member";
}

export function canDownloadModules(role?: string): boolean {
  const r = normalizeRole(role ?? effectiveUserRole());
  return r !== "viewer";
}

export function canDispatchWorkflow(role?: string): boolean {
  return canWrite(role);
}
